import { useState } from "react";
import {
  mfgCompactGhostBtnClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import {
  COLUMN_BAND_LABEL,
  COLUMN_BANDS,
  columnAffectsCalculatedRows,
  columnAppliesToProductsOnly,
  columnsInBand,
  columnCalcFromMetadata,
  columnCalcKind,
  columnStructureChanged,
  cogsCatalogSnapshotCsv,
  fieldsForColumnCalc,
  isFormulaKind,
} from "@/lib/manufacturing/cogsColumnEdit";
import { updateManufacturingPricingLayerColumn } from "@/lib/manufacturing/pricingLayersClient";
import type { ManufacturingPricingGridState } from "./useManufacturingPricingGrid";
import { columnAppliesRecipeIds, sameRecipeIdList } from "@/lib/manufacturing/cogsColumnProductScope";
import { AddPricingColumnForm } from "./ManufacturingPricingColumnAddForm";
import { PricingColumnEditor } from "./ManufacturingPricingColumnEditors";

type Mode = "add" | "change";

type PendingChange = {
  label: string;
  apply: () => Promise<void>;
};

export function ManufacturingPricingColumnsPanel({ grid }: { grid: ManufacturingPricingGridState }) {
  const [mode, setMode] = useState<Mode>("add");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const locked = busy || grid.actionBusy;

  const run = async (needsConfirm: boolean, label: string, apply: () => Promise<void>) => {
    if (needsConfirm) {
      setPending({ label, apply });
      return;
    }
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await apply();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Column change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div
          role="tablist"
          aria-label="Column mode"
          className="flex rounded-lg bg-gray-100 p-0.5 text-sm dark:bg-zinc-900"
        >
          {(
            [
              ["add", "Add"],
              ["change", "Change existing"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={mode === id}
              className={
                "rounded-md px-3 py-1 font-semibold " +
                (mode === id
                  ? "bg-white text-gray-900 shadow-sm dark:bg-zinc-950 dark:text-zinc-100"
                  : "text-gray-600 dark:text-zinc-400")
              }
              onClick={() => setMode(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Bands stay COGS, Wholesale, and MSRP. Set a margin to 0% when a company does not use that step.
        </p>
      </div>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {note ? <p className="text-sm text-gray-600 dark:text-zinc-300">{note}</p> : null}
      {mode === "add" ? (
        <AddPricingColumnForm
          locked={locked}
          columns={grid.columns}
          rows={grid.catalogRows}
          onError={setError}
          onAdded={() => grid.reloadGrid()}
        />
      ) : (
        <div className="space-y-4">
          {COLUMN_BANDS.map((band) => {
            const siblings = columnsInBand(
              grid.columns.filter((column) => column.is_active),
              band,
            );
            return (
              <section key={band} className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                  {COLUMN_BAND_LABEL[band]}
                </h3>
                {siblings.map((column, index) => (
                  <PricingColumnEditor
                    key={`${column.id}:${column.updated_at}:${column.sort_order}:${column.band}`}
                    column={column}
                    rows={grid.catalogRows}
                    locked={locked}
                    canUp={index > 0}
                    canDown={index < siblings.length - 1}
                    onMove={(direction) =>
                      void run(
                        siblings.some((item) => columnAffectsCalculatedRows(item.id, grid.catalogRows)),
                        `Reorder ${column.label}`,
                        async () => {
                          const next = siblings.slice();
                          const swap = index + direction;
                          [next[index], next[swap]] = [next[swap]!, next[index]!];
                          for (let i = 0; i < next.length; i++) {
                            const sort_order = (i + 1) * 10;
                            if (next[i]!.sort_order === sort_order) continue;
                            const saved = await updateManufacturingPricingLayerColumn({
                              column_id: next[i]!.id,
                              sort_order,
                            });
                            if (!saved.ok) throw new Error(saved.error);
                          }
                          await grid.reloadGrid();
                        },
                      )
                    }
                    onSave={(draft) => {
                      const fields = fieldsForColumnCalc(draft.calcKind, draft.band, column);
                      const next = { ...column, ...fields, band: draft.band, label: draft.label };
                      const sort_order =
                        draft.band === column.band
                          ? column.sort_order
                          : (columnsInBand(grid.columns, draft.band).at(-1)?.sort_order ?? 0) + 10;
                      const currentKind = columnCalcKind(column);
                      const wasFormula = isFormulaKind(currentKind);
                      const nowFormula = isFormulaKind(draft.calcKind);
                      const rateKindChanged = (currentKind === "markup") !== (draft.calcKind === "markup");
                      const rate_kind =
                        draft.calcKind === "markup" ? "markup" : currentKind === "markup" ? null : undefined;
                      const stored = columnCalcFromMetadata(column.metadata);
                      const nextCalc =
                        nowFormula && draft.rateUsd !== null ? `${draft.basis}:${draft.rateUsd}` : "";
                      const prevCalc = stored ? `${stored.basis}:${stored.rate_usd}` : "";
                      const formulaChanged = nextCalc !== prevCalc;
                      const productsOnlyChanged =
                        nowFormula && draft.productsOnly !== columnAppliesToProductsOnly(column);
                      const scopeChanged = !sameRecipeIdList(
                        nowFormula ? draft.recipeIds : null,
                        columnAppliesRecipeIds(column.metadata),
                      );
                      const calc =
                        nowFormula && draft.rateUsd !== null
                          ? { basis: draft.basis, rate_usd: draft.rateUsd }
                          : stored || (wasFormula && !nowFormula)
                            ? null
                            : undefined;
                      const applies_to = nowFormula
                        ? draft.productsOnly
                          ? "products"
                          : "all"
                        : wasFormula
                          ? null
                          : undefined;
                      const applies_recipe_ids = nowFormula
                        ? draft.recipeIds
                        : wasFormula
                          ? null
                          : undefined;
                      void run(
                        (columnStructureChanged(column, { ...next, sort_order }) ||
                          formulaChanged ||
                          productsOnlyChanged ||
                          scopeChanged ||
                          rateKindChanged) &&
                          columnAffectsCalculatedRows(column.id, grid.catalogRows),
                        column.label,
                        async () => {
                          const saved = await updateManufacturingPricingLayerColumn({
                            column_id: column.id,
                            label: draft.label,
                            band: draft.band,
                            sort_order,
                            ...fields,
                            ...(calc !== undefined ? { calc } : {}),
                            ...(applies_to !== undefined ? { applies_to } : {}),
                            ...(applies_recipe_ids !== undefined ? { applies_recipe_ids } : {}),
                            ...(rate_kind !== undefined ? { rate_kind } : {}),
                          });
                          if (!saved.ok) throw new Error(saved.error);
                          const missing = saved.data.missing_size_count ?? 0;
                          if (missing > 0) {
                            setNote(
                              `${missing} products have no size, so this column was left blank for them.`,
                            );
                          }
                          await grid.reloadGrid();
                        },
                      );
                    }}
                  />
                ))}
              </section>
            );
          })}
        </div>
      )}
      {pending ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="font-semibold">{pending.label} already has calculated COGS numbers.</p>
          <p className="mt-1">
            Changing it recalculates those rows. Download a snapshot of the current COGS first if you want the old numbers.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={mfgSecondaryBtnClass}
              onClick={() =>
                downloadCsv(
                  `cogs-before-${pending.label.replace(/\s+/g, "-").toLowerCase()}.csv`,
                  cogsCatalogSnapshotCsv(grid.columns, grid.catalogRows),
                )
              }
            >
              Download COGS snapshot
            </button>
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              disabled={locked}
              onClick={() => {
                const apply = pending.apply;
                setPending(null);
                void run(false, "", apply);
              }}
            >
              Change column
            </button>
            <button type="button" className={mfgCompactGhostBtnClass} onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
